const MODE_BULK_ADD = 'bulk_add';
const MODE_REPLACE_ALL = 'replace_all';
const MAX_STAGED_VARIANTS = 200;

const normalizeSku = sku => (sku || '')
  .trim()
  .replace(/\s+/g, ' ')
  .toUpperCase();

const buildInitialState = () => ({
  mode: MODE_BULK_ADD,
  stagedVariants: [],
  csvParsing: false,
  csvValidating: false,
  csvValidationCount: 0,
  csvValidationResults: null,
  showCsvResults: false,
  csvError: null,
  csvSuccessMessage: null,
  selectionError: null,
  isSubmitting: false,
  submitError: null,
  isReplaceConfirmationOpen: false
});

const mapVariantToOption = item => ({
  id: item.id,
  sku: item.SKU,
  name: `${item.SKU} - ${item.display_name}`,
  display_name: item.display_name,
  alreadyExists: false
});

const dedupeStagedVariants = (selected) => {
  const uniqueMap = new Map();

  (selected || []).forEach(item => {
    const normalizedSku = normalizeSku(item.sku || item.SKU || item.name);
    if (!normalizedSku || uniqueMap.has(normalizedSku)) return;

    uniqueMap.set(normalizedSku, {
      ...item,
      sku: item.sku || item.SKU || normalizedSku,
      name: item.name || `${item.sku || item.SKU || normalizedSku} - ${item.display_name || ''}`.trim(),
      alreadyExists: !!item.alreadyExists
    });
  });

  return Array.from(uniqueMap.values());
};

const extractCsvEntries = (rows, stagedVariants) => {
  const stagedSkuSet = new Set(stagedVariants.map(item => normalizeSku(item.sku)));
  const invalid = [];
  const unresolvedSkuMap = new Map();

  rows.forEach((row, index) => {
    const normalizedSku = normalizeSku(row);
    const cleanedSku = (row || '').trim().replace(/\s+/g, ' ');

    if (!normalizedSku) {
      return;
    }

    if (stagedSkuSet.has(normalizedSku) || unresolvedSkuMap.has(normalizedSku)) {
      invalid.push({ sku: cleanedSku || `(row ${index + 1})`, reason: 'Duplicate SKU inside current bulk staging set' });
      return;
    }

    unresolvedSkuMap.set(normalizedSku, {
      sku: cleanedSku || row,
      normalizedSku
    });
  });

  return {
    invalid,
    unresolvedEntries: Array.from(unresolvedSkuMap.values())
  };
};

const countMeaningfulCsvRows = rows => rows.reduce((count, row) => {
  return normalizeSku(row) ? count + 1 : count;
}, 0);

const mergeValidatedVariants = (stagedVariants, csvValidationResults) => {
  const mergedMap = new Map();

  stagedVariants.forEach(item => {
    mergedMap.set(normalizeSku(item.sku), item);
  });

  [...csvValidationResults.valid, ...csvValidationResults.alreadyExists].forEach(item => {
    const normalizedSku = normalizeSku(item.sku);
    if (!normalizedSku || mergedMap.has(normalizedSku)) return;

    mergedMap.set(normalizedSku, {
      ...item,
      alreadyExists: !!item.alreadyExists
    });
  });

  return Array.from(mergedMap.values());
};

const buildStagedSummary = (stagedVariants, mode) => {
  const valid = [];
  const alreadyExists = [];

  stagedVariants.forEach(item => {
    if (item.alreadyExists) {
      alreadyExists.push(item);
      return;
    }

    valid.push(item);
  });

  return {
    valid,
    alreadyExists,
    submitEligible: mode === MODE_REPLACE_ALL
      ? [...valid, ...alreadyExists]
      : valid
  };
};

export {
  buildInitialState,
  buildStagedSummary,
  countMeaningfulCsvRows,
  dedupeStagedVariants,
  extractCsvEntries,
  MAX_STAGED_VARIANTS,
  mapVariantToOption,
  mergeValidatedVariants,
  MODE_BULK_ADD,
  MODE_REPLACE_ALL,
  normalizeSku
};
